import { Box, Button } from "@mui/material";
import BasicFormikTextField from "./BasicFormikTextField";
import { BasicFormikCheckbox } from "./BasicFormikCheckbox";
import { BasicFormikSelect } from "./BasicFormikSelect";
import { BasicFormikNumber } from "./BasicFormikNumber";
import { _FORM_BTM } from "../../utils/constants";
import { BasicFormikColorField } from "./BasicFormikColorField";
import { BasicFormikAutocomplete } from "./BasicFormikAutocomplete";
import React from "react";

export const BasicFormikForm = ({
  formik,
  validations,
  options,
  setEditData = () => {},
  setShowModal = null,
}) => {
  const fields = formik.values;
  const customStructure = formik?.special?.customStructure;
  const autoComplete = formik?.special?.autocomplete;
  const customComponents = formik?.special?.customComponents || {};
  const multipleSelect = formik?.special?.multipleSelect || {};
  const layout = formik?.special?.layout || null;
  const lineDisposition = layout?.lineDisposition || "space-between";
  const childrens = layout?.childrens || null;

  const linesDone = [];
  const getElement = (
    fieldType,
    key,
    field,
    placeholder,
    helperText,
    isSelect,
    isColor,
  ) => {
    // Check if this field has a custom component defined
    if (customComponents[key]) {
      // If the custom component is a React element (JSX), render it directly
      if (React.isValidElement(customComponents[key])) {
        // Clone the element to add a key prop
        return React.cloneElement(customComponents[key], { key });
      }

      // Otherwise, treat it as a component function
      const CustomComponent = customComponents[key];
      return (
        <CustomComponent
          key={key}
          value={formik.values[key]}
          onChange={(value) => formik.setFieldValue(key, value)}
          error={formik.touched[key] && Boolean(formik.errors[key])}
          helperText={
            formik.touched[key] && formik.errors[key]
              ? formik.errors[key]
              : helperText
          }
          label={field.spec.label}
          placeholder={placeholder}
          id={key}
        />
      );
    }

    const layout = formik?.special?.layout || null;
    const disabled = formik?.special?.disabled || {}; 

    // Check if this field should support multiple selections
    const isMultiSelect = multipleSelect[key] || fieldType === "array";
    // Check if this field should be disabled
    const isDisabled = disabled[key] === true; 

    switch (fieldType) {
      case "string":
        if (isColor) {
          return (
            // COLOR PICKER FIELDS
            <BasicFormikColorField
              key={key}
              fieldId={key}
              field={field}
              formik={formik}
              labelAlignement={layout?.[key]?.enableLeftLabel}
              disabled={isDisabled}
            />
          );
        }

        return isSelect ? (
          // SELECT STRING FIELDS
          <BasicFormikSelect
            key={key}
            fieldId={key}
            field={field}
            formik={formik}
            options={options[key]}
            placeholder={placeholder}
            helperText={helperText}
            labelAlignement={layout?.[key]?.enableLeftLabel}
            multiple={isMultiSelect}
            disabled={isDisabled || layout?.[key]?.disabled}
          />
        ) : (
          // TEXT FIELDS
          <BasicFormikTextField
            key={key}
            fieldId={key}
            field={field}
            formik={formik}
            placeholder={placeholder}
            helperText={helperText}
            labelAlignement={layout?.[key]?.enableLeftLabel}
            disabled={isDisabled}
          />
        );
      case "number":
        return (
          // TEXT FIELDS
          <BasicFormikNumber
            key={key}
            fieldId={key}
            field={field}
            formik={formik}
            placeholder={placeholder}
            helperText={helperText}
            labelAlignement={layout?.[key]?.enableLeftLabel}
            disabled={isDisabled || layout?.[key]?.disabled}
          />
        );
      case "array":
        const autocompleteProps =
          autoComplete && key in autoComplete ? autoComplete[key] : false;

        return autocompleteProps ? (
          // AUTOCOMPLETE FIELDS
          <BasicFormikAutocomplete
            key={key}
            fieldId={key}
            field={field}
            formik={formik}
            options={options[key]}
            placeholder={placeholder}
            helperText={helperText}
            multiple={autocompleteProps.multiple || isMultiSelect}
            labelAlignement={layout?.[key]?.enableLeftLabel}
            disabled={layout?.[key]?.disabled}
          />
        ) : (
          // MULTI SELECT FIELDS
          <BasicFormikSelect
            key={key}
            multiple={true}
            fieldId={key}
            field={field}
            formik={formik}
            options={options[key]}
            placeholder={placeholder}
            helperText={helperText}
            labelAlignement={layout?.[key]?.enableLeftLabel}
            disabled={layout?.[key]?.disabled}
          />
        );
      case "boolean":
        return (
          // CHECKBOX FIELDS
          <BasicFormikCheckbox
            key={key}
            fieldId={key}
            field={field}
            formik={formik}
            disabled={isDisabled}
          />
        );
      case "textarea":
        return (
          // TEXTAREA FIELDS
          <BasicFormikTextField
            key={key}
            fieldId={key}
            field={field}
            formik={formik}
            placeholder={placeholder}
            helperText={helperText}
            labelAlignement={layout?.[key]?.enableLeftLabel}
            disabled={isDisabled}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <form id="basicForm" onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
          }}
        >
          {Object.keys(fields).map((key) => {
            // Skip rendering if the field is not in the validation schema
            if (!validations.fields[key]) return null;

            const field = validations.fields[key];
            const fieldType = field.type;
            const isSelect = options ? key in options : false;
            const isColor = key.includes("color");
            const placeholder = formik?.special?.placeholder?.[key];
            const helperText = formik?.special?.helperText?.[key];
            const labelAlignement = layout?.[key]?.enableLeftLabel;

            if (customStructure) {
              const line = Object.values(customStructure).find((tmp) =>
                tmp.includes(key),
              );
              if (line) {
                if (linesDone.includes(line)) return null;
                const index = line.indexOf(key);
                const isAlone = line.length === 1;
                const isStart = index === 0;
                const isEnd = index === line.length - 1;
                const isMiddle = !isStart && !isEnd;
                linesDone.push(line);
                return (
                  <Box
                    key={key}
                    sx={{
                      display: "flex",
                      gap: 2,
                      width: "100%",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: !labelAlignement
                        ? lineDisposition
                        : "start",
                    }}
                  >
                    {line.map((fieldKey) =>
                      getElement(
                        validations.fields[fieldKey].type,
                        fieldKey,
                        validations.fields[fieldKey],
                        formik?.special?.placeholder?.[fieldKey],
                        formik?.special?.helperText?.[fieldKey],
                        options ? fieldKey in options : false,
                        fieldKey.includes("color"),
                      ),
                    )}
                    {childrens && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "start",
                        }}
                      >
                        {childrens}
                      </Box>
                    )}
                  </Box>
                );
              }
            }

            return (
              <Box key={key}>
                {getElement(
                  fieldType,
                  key,
                  field,
                  placeholder,
                  helperText,
                  isSelect,
                  isColor,
                )}
              </Box>
            );
          })}
        </Box>
        {setShowModal && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                formik.resetForm();
                setEditData(null);
                setShowModal(false);
              }}
            >
              {_FORM_BTM.cancel}
            </Button>
            <Button variant="contained" color="success" type="submit">
              {_FORM_BTM.save}
            </Button>
          </Box>
        )}
      </form>
    </>
  );
};
